import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, LayoutGrid, KeyRound, ScrollText, ShieldCheck, Boxes, Mail, Sparkles } from "lucide-react";
import NovitaTab from "@/pages/admin/NovitaTab";
import UsersTab from "@/pages/admin/UsersTab";
import AppsTab from "@/pages/admin/AppsTab";
import PermissionsTab from "@/pages/admin/PermissionsTab";
import LogsTab from "@/pages/admin/LogsTab";
import CatalogoTab from "@/pages/admin/CatalogoTab";
import ModelliEmailTab from "@/pages/admin/ModelliEmailTab";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [apps, setApps] = useState([]);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState("");

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  const loadApps = useCallback(async () => {
    const { data } = await api.get("/apps");
    setApps(data);
  }, []);

  const loadLogs = useCallback(async () => {
    const { data } = await api.get("/logs");
    setLogs(data);
  }, []);

  useEffect(() => {
    loadUsers();
    loadApps();
    loadLogs();
  }, [loadUsers, loadApps, loadLogs]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8 flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-brand-blue" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Area riservata</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-medium text-slate-900 tracking-tight">
              Pannello Amministratore
            </h1>
          </div>
        </div>

        {error && <p className="mb-4 text-sm text-red-500">{error}</p>}

        <Tabs defaultValue="users" className="w-full">
          <TabsList data-testid="admin-tabs" className="bg-white border border-slate-200 mb-6 h-auto flex-wrap">
            <TabsTrigger value="users" data-testid="tab-users" className="gap-2">
              <Users className="h-4 w-4" /> Utenti
            </TabsTrigger>
            <TabsTrigger value="apps" data-testid="tab-apps" className="gap-2">
              <LayoutGrid className="h-4 w-4" /> App
            </TabsTrigger>
            <TabsTrigger value="catalogo" data-testid="tab-catalogo" className="gap-2">
              <Boxes className="h-4 w-4" /> Catalogo Prodotti
            </TabsTrigger>
            <TabsTrigger value="modelli" data-testid="tab-modelli-email" className="gap-2">
              <Mail className="h-4 w-4" /> Modelli email
            </TabsTrigger>
            <TabsTrigger value="permissions" data-testid="tab-permissions" className="gap-2">
              <KeyRound className="h-4 w-4" /> Permessi
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs" className="gap-2">
              <ScrollText className="h-4 w-4" /> Log accessi
            </TabsTrigger>
            <TabsTrigger value="novita" data-testid="tab-novita" className="gap-2">
              <Sparkles className="h-4 w-4" /> Novità versione
            </TabsTrigger>
          </TabsList>
          <TabsContent value="novita">
            <NovitaTab />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab users={users} apps={apps} reload={loadUsers} />
          </TabsContent>
          <TabsContent value="apps">
            <AppsTab apps={apps} reload={loadApps} />
          </TabsContent>
          <TabsContent value="catalogo">
            <CatalogoTab />
          </TabsContent>
          <TabsContent value="modelli">
            <ModelliEmailTab />
          </TabsContent>
          <TabsContent value="permissions">
            <PermissionsTab users={users} apps={apps} reload={loadUsers} />
          </TabsContent>
          <TabsContent value="logs">
            <LogsTab logs={logs} reload={loadLogs} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
